import React, { useRef, useState } from 'react';

import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    MenuItem,
    Popover,
    Slider,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { defaultExportOptions, ExportManager } from '../../../../Utils/ExportManager';
import LayerList from '../LayerList';
import { getDisabledInEnvMessage, useEnvStore } from 'store/EnvStore';
import CustomTooltip from '@gui/components/generic/CustomTooltip';
import CoordinateSystemSelectorControl from '@gui/components/generic/CoordinateSystemSelectorControl';
import { SIM_SUN4D } from '@visualizer/types';
import { COLUMN_WIDTH } from './SidePaneDialog';
import { getGrid, useLoadingStore } from 'store/LoadingStore';
import { VideoExportVariant, ResolutionPreset, VideoExportFormat, RESOLUTION_PRESETS } from 'Utils/VideoExporter';
import { useSessionStore } from '@gui/store/session/uiSessionStore';
import { useGlobalBucket } from '@gui/store/persistent/persistentStore';
import { useCapabilities } from '../../../../demo/capabilities';
import { useSessionStore as useAuthSessionStore } from '../../../../store/SessionStore';
import { getSentence } from '@gui/constants/sentences';

interface DialogSizing {
    showContent: boolean;
}

type ExportProps = DialogSizing;

type SaveLocation = 'computer' | 'cloud' | 'both';

const Export: React.FC<ExportProps> = ({ showContent }) => {
    const { canExportToCloud } = useCapabilities();
    const [exportMode, setExportMode] = useState('export3DModel');

    const [export3DVariant, setExport3DVariant] = useState('currentView');
    const [exportVideoVariant, setExportVideoVariant] = useState<VideoExportVariant>('orbit');
    const [exportScreenshotVariant, setExportScreenshotVariant] = useState('fullHD');
    const [exportScreenshotGuiMode, setExportScreenshotGuiMode] = useState<'without' | 'with' | 'both'>('without');
    const [exportTextDataVariant, setExportTextDataVariant] = useState('currentView');

    const [export3DFormat, setExport3DFormat] = useState('3DM');
    const [exportVideoFormat, setExportVideoFormat] = useState<VideoExportFormat>('MP4');
    const [exportScreenshotFormat, setExportScreenshotFormat] = useState('PNG');
    const [exportTextDataFormat, setExportTextDataFormat] = useState('CSV');

    const [saveLocation, setSaveLocation] = useState<SaveLocation>('computer');
    const [individualMeshes, setIndividualMeshes] = useState(false);
    const [videoQuality, setVideoQuality] = useState(80);
    const [easeInAnimation, setEaseInAnimation] = useState(true);

    const export3DSelectRef = useRef<HTMLDivElement | null>(null);
    const exportTextSelectRef = useRef<HTMLDivElement | null>(null);
    const [showEverythingWarning, setShowEverythingWarning] = useState(false);
    const [warningAnchor, setWarningAnchor] = useState<HTMLElement | null>(null);
    const [pendingVariant, setPendingVariant] = useState<{ target: '3d' | 'text'; value: string } | null>(null);

    const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
    const env = useEnvStore((state) => state.env);
    const webappUrl = useEnvStore((state) => state.backendUrl);
    const hasVideoAndNumericalExports = useAuthSessionStore((state) => state.hasVideoAndNumericalExports);
    const billingPageUrl = `${webappUrl}/account/subscription`;
    const planFeatureDisabledTooltip = (
        <>
            {getSentence('Plan.Feature.Disabled.Tooltip')}{' '}
            <a href={billingPageUrl} target="_blank" rel="noreferrer" style={{ color: '#3b82f6', textDecoration: 'underline' }}>
                Go to billing page
            </a>
        </>
    );

    const setLoadingMessage = useSessionStore((s) => s.setLoadingMessage);
    const coordinateSystem = useGlobalBucket((s) => s.settings.coordinateSystem);

    const exportManagerRef = React.useRef<ExportManager | null>(null);
    if (!exportManagerRef.current) {
        exportManagerRef.current = new ExportManager(setLoadingMessage);
    }
    const exportManager = exportManagerRef.current;

    const isRecording = useLoadingStore((s) => s.freeScreenRecording?.active ?? false);

    // -------------------------
    // Video Advanced Settings
    // -------------------------
    const [videoAdvancedOpen, setVideoAdvancedOpen] = useState(false);

    // Duration in seconds
    const [videoDurationSecInput, setVideoDurationSecInput] = useState('5');

    // FPS options: keep it simple
    const [videoFps, setVideoFps] = useState(25);

    // Resolution preset
    const [videoResolution, setVideoResolution] = useState<ResolutionPreset>('1080p');

    // DPR cap (usually 1)
    const [videoDprCap, setVideoDprCap] = useState(1);

    // ---------------------------
    // 3D Model Advanced Settings
    // ---------------------------
    const [modelAdvancedOpen, setModelAdvancedOpen] = useState(false);

    // 3D orientation options
    const [orientation3d, setOrientation3d] = useState<'Y_Up' | 'Z_Up'>('Y_Up');
    const supportsOrientation3d = export3DFormat === 'STL' || export3DFormat === 'DXF';

    const isActiveLayer4D = getGrid()?.getActiveLayer?.()?.isOfType?.(SIM_SUN4D) ?? false;

    function maybeWarnAndSet(target: '3d' | 'text', value: string, currentValue: string, anchorEl: HTMLElement | null) {
        if (value === 'everything' && currentValue !== 'everything') {
            setPendingVariant({ target, value });
            setShowEverythingWarning(true);
            setWarningAnchor(anchorEl);
            return true;
        }
        return false;
    }

    function handleExport3DVariantChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const value = event.target.value as string;
        if (maybeWarnAndSet('3d', value, export3DVariant, export3DSelectRef.current)) return;
        setExport3DVariant(value);
    }

    function handleExportTextVariantChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const value = event.target.value as string;
        if (maybeWarnAndSet('text', value, exportTextDataVariant, exportTextSelectRef.current)) return;
        setExportTextDataVariant(value);
    }

    function handleExportModeChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const value = event.target.value as string;
        const isPlanBlockedMode = !hasVideoAndNumericalExports && (value === 'exportVideo' || value === 'exportTextData');

        if (isPlanBlockedMode) {
            return;
        }

        setExportMode(value);
    }

    function handleConfirmEverythingSelection() {
        if (pendingVariant) {
            if (pendingVariant.target === '3d') setExport3DVariant(pendingVariant.value);
            if (pendingVariant.target === 'text') setExportTextDataVariant(pendingVariant.value);
        }
        setPendingVariant(null);
        setShowEverythingWarning(false);
        setWarningAnchor(null);
    }

    function handleCancelEverythingSelection() {
        setPendingVariant(null);
        setShowEverythingWarning(false);
        setWarningAnchor(null);
    }

    React.useEffect(() => {
        if (!isActiveLayer4D && exportVideoVariant === 'timelapse') {
            setExportVideoVariant('orbit');
        }
    }, [isActiveLayer4D, exportVideoVariant]);

    React.useEffect(() => {
        if (!supportsOrientation3d) {
            setModelAdvancedOpen(false);
            setOrientation3d('Y_Up');
        }
    }, [supportsOrientation3d]);

    React.useEffect(() => {
        if (!hasVideoAndNumericalExports && (exportMode === 'exportVideo' || exportMode === 'exportTextData')) {
            setExportMode('export3DModel');
        }
    }, [exportMode, hasVideoAndNumericalExports]);

    const handleClick = async () => {
        const saveOnComputer = saveLocation !== 'cloud';
        const saveOnCloud = saveLocation !== 'computer';

        switch (exportMode) {
            case 'export3DModel':
                await exportManager.export3DModel(
                    {
                        ...defaultExportOptions,
                        variant: export3DVariant,
                        format: export3DFormat,
                        download: saveOnComputer,
                        upload: saveOnCloud,
                        individualMeshes,
                        coordinateSystem,
                        orientation3d: supportsOrientation3d && modelAdvancedOpen ? orientation3d : 'Y_Up',
                    },
                    selectedItems
                );
                break;

            case 'exportVideo': {
                const durationSec = Math.max(1, Math.min(60, Number(videoDurationSecInput === '' ? '5' : videoDurationSecInput)));

                const opts = {
                    ...defaultExportOptions,
                    variant: exportVideoVariant,
                    format: exportVideoFormat,
                    download: saveOnComputer,
                    upload: saveOnCloud,
                    quality: videoQuality,
                    easeInAnimation,
                    videoSettings: videoAdvancedOpen
                        ? {
                              durationSec: exportVideoVariant === 'screenrecording' ? undefined : durationSec,
                              fps: videoFps,
                              resolution: videoResolution,
                              dprCap: videoDprCap,
                              keepViewerSize: true,
                          }
                        : undefined,
                };

                if (exportVideoVariant === 'screenrecording') {
                    if (!isRecording) {
                        await exportManager.startFreeScreenRecording(opts);
                    } else {
                        await exportManager.stopFreeScreenRecordingAndExport();
                    }
                } else {
                    await exportManager.exportVideo(opts);
                }
                break;
            }

            case 'exportScreenshot':
                await exportManager.exportScreenshot({
                    ...defaultExportOptions,
                    variant: exportScreenshotVariant,
                    format: exportScreenshotFormat,
                    download: saveOnComputer,
                    upload: saveOnCloud,
                    guiMode: exportScreenshotGuiMode,
                });
                break;

            case 'exportTextData':
                await exportManager.exportTextData(
                    {
                        ...defaultExportOptions,
                        variant: exportTextDataVariant,
                        format: exportTextDataFormat,
                        download: saveOnComputer,
                        upload: saveOnCloud,
                        coordinateSystem,
                    },
                    selectedItems
                );
                break;
        }
    };

    if (!showContent) return null;

    return (
        <>
            <FormControl component="fieldset" fullWidth sx={{ mb: 2 }}>
                <TextField select size="small" label="File Type" value={exportMode} onChange={handleExportModeChange}>
                    <MenuItem value="export3DModel">3D Model</MenuItem>
                    <MenuItem
                        value="exportVideo"
                        aria-disabled={!hasVideoAndNumericalExports}
                        sx={
                            !hasVideoAndNumericalExports
                                ? {
                                      color: 'text.disabled',
                                      opacity: 0.6,
                                      cursor: 'not-allowed',
                                  }
                                : undefined
                        }
                    >
                        <CustomTooltip side="left" fullWidth title={!hasVideoAndNumericalExports ? planFeatureDisabledTooltip : ''}>
                            <Box sx={{ width: '100%', display: 'block' }}>Video</Box>
                        </CustomTooltip>
                    </MenuItem>
                    <MenuItem value="exportScreenshot">Image</MenuItem>
                    <MenuItem
                        value="exportTextData"
                        aria-disabled={!hasVideoAndNumericalExports}
                        sx={
                            !hasVideoAndNumericalExports
                                ? {
                                      color: 'text.disabled',
                                      opacity: 0.6,
                                      cursor: 'not-allowed',
                                  }
                                : undefined
                        }
                    >
                        <CustomTooltip side="left" fullWidth title={!hasVideoAndNumericalExports ? planFeatureDisabledTooltip : ''}>
                            <Box sx={{ width: '100%', display: 'block' }}>Sensor Point Data</Box>
                        </CustomTooltip>
                    </MenuItem>
                </TextField>
            </FormControl>

            {exportMode === 'export3DModel' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} id="export-3d-section">
                    <Box ref={export3DSelectRef}>
                        <TextField
                            id="export-3d-content"
                            select
                            size="small"
                            fullWidth
                            label="Select Content"
                            value={export3DVariant}
                            onChange={handleExport3DVariantChange}
                        >
                            <MenuItem value="currentView" id="export-3d-content-currentView">
                                Visible Scene
                            </MenuItem>
                            <MenuItem value="everything" id="export-3d-content-everything">
                                All Geodata and Simulations
                            </MenuItem>
                            <MenuItem value="custom" id="export-3d-content-custom">
                                Custom Selection
                            </MenuItem>
                        </TextField>
                    </Box>

                    {export3DVariant === 'custom' && (
                        <Box sx={{ mb: 1 }} id="export-3d-custom-selection">
                            <LayerList selectedItems={selectedItems} setSelectedItems={setSelectedItems} />
                        </Box>
                    )}

                    <Box sx={{ mt: 1 }} id="export-coordinate-system">
                        <CoordinateSystemSelectorControl title="Coordinates System" />
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={individualMeshes}
                                onChange={(event) => setIndividualMeshes(event.target.checked)}
                                size="small"
                                id="export-3d-individual-meshes"
                            />
                        }
                        label="Split Buildings and Trees in single meshes"
                        sx={{ mb: 1 }}
                    />

                    <TextField
                        id="export-3d-format"
                        select
                        size="small"
                        fullWidth
                        label="Format"
                        value={export3DFormat}
                        onChange={(event) => setExport3DFormat(event.target.value)}
                    >
                        <MenuItem value="3DM" id="export-3d-format-3dm">
                            3DM (Rhino)
                        </MenuItem>
                        <MenuItem value="OBJ" id="export-3d-format-obj">
                            OBJ
                        </MenuItem>
                        <MenuItem value="STL" id="export-3d-format-stl">
                            STL
                        </MenuItem>
                        <MenuItem value="GLTF" id="export-3d-format-gltf">
                            GLTF
                        </MenuItem>
                        <MenuItem value="DXF" id="export-3d-format-dxf">
                            DXF (Cloud only)
                        </MenuItem>
                    </TextField>

                    {supportsOrientation3d && (
                        <>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        size="small"
                                        checked={modelAdvancedOpen}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            setModelAdvancedOpen(checked);

                                            if (!checked) {
                                                setOrientation3d('Y_Up');
                                            }
                                        }}
                                        id="export-3d-advanced-toggle"
                                    />
                                }
                                label="Advanced settings"
                            />

                            {modelAdvancedOpen && (
                                <Box
                                    sx={{ p: 1, border: '1px solid rgba(255,255,255,0.12)', borderRadius: 1 }}
                                    id="export-3d-advanced-panel"
                                >
                                    <TextField
                                        id="export-3d-orientation"
                                        select
                                        size="small"
                                        fullWidth
                                        label="Orientation"
                                        value={orientation3d}
                                        onChange={(e) => setOrientation3d(e.target.value as 'Y_Up' | 'Z_Up')}
                                    >
                                        <MenuItem value="Y_Up" id="export-3d-orientation-y">
                                            Y-Up
                                        </MenuItem>
                                        <MenuItem value="Z_Up" id="export-3d-orientation-z">
                                            Z-Up
                                        </MenuItem>
                                    </TextField>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            )}

            {exportMode === 'exportVideo' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        select
                        size="small"
                        fullWidth
                        label="Video Type"
                        value={exportVideoVariant}
                        onChange={(event) => setExportVideoVariant(event.target.value as VideoExportVariant)}
                    >
                        <MenuItem value="orbit">360 Orbit</MenuItem>
                        <MenuItem value="lookaround">360 Look-Around</MenuItem>
                        <MenuItem value="timelapse" disabled={!isActiveLayer4D}>
                            Frames Timelapse
                        </MenuItem>
                        <MenuItem value="screenrecording">Free Screen Recording</MenuItem>
                    </TextField>

                    {(exportVideoVariant === 'lookaround' || exportVideoVariant === 'orbit') && (
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={easeInAnimation}
                                    onChange={(event) => setEaseInAnimation(event.target.checked)}
                                />
                            }
                            label="Soften speed at start and end"
                        />
                    )}

                    <CustomTooltip
                        side="left"
                        mode="icon"
                        iconOffset={8}
                        title="Warning : GIF is unable to render all colors of the SensorData display (especially in the blues and greens) and may generate blinking Sensor Points"
                    >
                        <Box sx={{ width: '100%' }}>
                            <TextField
                                select
                                size="small"
                                fullWidth
                                label="Format"
                                value={exportVideoFormat}
                                onChange={(event) => setExportVideoFormat(event.target.value as VideoExportFormat)}
                            >
                                <MenuItem value="MP4">MP4 video</MenuItem>
                                <MenuItem value="GIF">Animated Gif</MenuItem>
                            </TextField>
                        </Box>
                    </CustomTooltip>

                    <Box>
                        <Typography variant="body2" gutterBottom>
                            Quality: {videoQuality}
                        </Typography>
                        <Slider
                            className="panel-slider"
                            value={videoQuality}
                            size="small"
                            min={0}
                            max={100}
                            step={20}
                            marks
                            valueLabelDisplay="on"
                            onChange={(_, value) => setVideoQuality(Array.isArray(value) ? value[0] : value)}
                        />
                    </Box>

                    <FormControlLabel
                        control={
                            <Checkbox size="small" checked={videoAdvancedOpen} onChange={(e) => setVideoAdvancedOpen(e.target.checked)} />
                        }
                        label="Advanced settings"
                    />

                    {videoAdvancedOpen && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                                p: 1,
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: 1,
                            }}
                        >
                            {exportVideoVariant !== 'screenrecording' ? (
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="number"
                                    label="Duration (sec)"
                                    value={videoDurationSecInput}
                                    inputProps={{ min: 1, max: 60, step: 1 }}
                                    onChange={(e) => {
                                        const v = e.target.value;
                                        // allow empty while editing
                                        if (v === '') {
                                            setVideoDurationSecInput('');
                                            return;
                                        }
                                        // allow only digits / valid number typing
                                        setVideoDurationSecInput(v);
                                    }}
                                    onBlur={() => {
                                        const n = Number(videoDurationSecInput);
                                        if (!Number.isFinite(n) || n < 1) setVideoDurationSecInput('1');
                                        else if (n > 60) setVideoDurationSecInput('60');
                                        else setVideoDurationSecInput(String(Math.round(n)));
                                    }}
                                />
                            ) : null}

                            <TextField
                                select
                                size="small"
                                fullWidth
                                label="FPS"
                                value={videoFps}
                                onChange={(e) => setVideoFps(Number(e.target.value))}
                            >
                                <MenuItem value={24}>24</MenuItem>
                                <MenuItem value={25}>25</MenuItem>
                                <MenuItem value={30}>30</MenuItem>
                            </TextField>

                            <TextField
                                select
                                size="small"
                                fullWidth
                                label="Resolution"
                                value={videoResolution}
                                onChange={(event) => setVideoResolution(event.target.value as ResolutionPreset)}
                            >
                                {Object.entries(RESOLUTION_PRESETS).map(([key, preset]) => (
                                    <MenuItem key={key} value={key}>
                                        {preset.label}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                size="small"
                                fullWidth
                                label="DPR Cap"
                                value={videoDprCap}
                                onChange={(e) => setVideoDprCap(Number(e.target.value))}
                            >
                                <MenuItem value={1}>1 (recommended)</MenuItem>
                                <MenuItem value={2}>2 (retina)</MenuItem>
                            </TextField>
                        </Box>
                    )}
                </Box>
            )}

            {exportMode === 'exportScreenshot' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        select
                        size="small"
                        fullWidth
                        label="Resolution"
                        value={exportScreenshotVariant}
                        onChange={(event) => setExportScreenshotVariant(event.target.value)}
                    >
                        <MenuItem value="screenres">Current Screen Resolution</MenuItem>
                        <MenuItem value="HD">HD (1280 x 720)</MenuItem>
                        <MenuItem value="fullHD">Full HD (1920 x 1080)</MenuItem>
                        <MenuItem value="4k">4k Resolution (3840 x 2160)</MenuItem>
                        <MenuItem value="veryhighres">Max PC Resolution</MenuItem>
                    </TextField>

                    <TextField
                        select
                        size="small"
                        fullWidth
                        label="GUI with options"
                        value={exportScreenshotGuiMode}
                        onChange={(event) => setExportScreenshotGuiMode(event.target.value as 'without' | 'with' | 'both')}
                    >
                        <MenuItem value="without">Without GUI</MenuItem>
                        <MenuItem value="with">With GUI</MenuItem>
                        <MenuItem value="both">With and Without GUI</MenuItem>
                    </TextField>

                    <TextField
                        select
                        size="small"
                        fullWidth
                        label="Format"
                        value={exportScreenshotFormat}
                        onChange={(event) => setExportScreenshotFormat(event.target.value)}
                    >
                        <MenuItem value="PNG">PNG</MenuItem>
                        <MenuItem value="JPG">JPG</MenuItem>
                    </TextField>
                </Box>
            )}

            {exportMode === 'exportTextData' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box ref={exportTextSelectRef}>
                        <TextField
                            select
                            size="small"
                            fullWidth
                            label="Select Content"
                            value={exportTextDataVariant}
                            onChange={handleExportTextVariantChange}
                        >
                            <MenuItem value="currentView">Visible Sensor Point Data</MenuItem>
                            <MenuItem value="everything">All Sensor Point Data</MenuItem>
                            <MenuItem value="custom">Custom Selection</MenuItem>
                        </TextField>
                    </Box>

                    {exportMode === 'exportTextData' && exportTextDataVariant === 'custom' && (
                        <Box sx={{ mb: 1 }}>
                            <LayerList selectedItems={selectedItems} setSelectedItems={setSelectedItems} />
                        </Box>
                    )}

                    <CoordinateSystemSelectorControl title="Coordinates System" />

                    <TextField
                        select
                        size="small"
                        fullWidth
                        label="Format"
                        value={exportTextDataFormat}
                        onChange={(event) => setExportTextDataFormat(event.target.value)}
                    >
                        <MenuItem value="CSV">Comma Separated Values .csv</MenuItem>
                        <MenuItem value="TSV">Tab Separated Values .tsv</MenuItem>
                        <MenuItem value="JSON">JSON .json</MenuItem>
                    </TextField>
                </Box>
            )}

            <Tooltip title={!canExportToCloud ? getDisabledInEnvMessage(env) : ''}>
                <span style={{ width: '100%' }}>
                    <TextField
                        select
                        sx={{ mt: 2, mb: 1.5 }}
                        size="small"
                        fullWidth
                        label="Save Destination"
                        value={!canExportToCloud ? 'computer' : saveLocation}
                        disabled={env === 'local-offline' || !canExportToCloud}
                        onChange={(event) => setSaveLocation(event.target.value as SaveLocation)}
                    >
                        <MenuItem value="computer">Save on Computer</MenuItem>
                        {canExportToCloud && <MenuItem value="cloud">Save on Cloud</MenuItem>}
                        {canExportToCloud && <MenuItem value="both">Save on Computer and Cloud</MenuItem>}
                    </TextField>
                </span>
            </Tooltip>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button variant="contained" onClick={handleClick}>
                    {exportMode === 'exportVideo' && exportVideoVariant === 'screenrecording'
                        ? isRecording
                            ? 'Stop Recording & Export'
                            : 'Start Recording'
                        : 'Export'}
                </Button>
            </Box>

            <Popover
                open={showEverythingWarning}
                onClose={handleCancelEverythingSelection}
                anchorEl={warningAnchor}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        p: 2,
                        maxWidth: `${COLUMN_WIDTH - 24}px`,
                        width: '100%',
                        boxShadow: 4,
                    },
                }}
            >
                <Typography variant="body1" sx={{ mb: 2 }}>
                    Warning, this could generate very large files.
                </Typography>
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button variant="text" onClick={handleCancelEverythingSelection}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleConfirmEverythingSelection}>
                        Ok
                    </Button>
                </Stack>
            </Popover>
        </>
    );
};

export default Export;
